import * as React from "react";
import Bowser from "bowser";
import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Auth0Provider, useAuth0, AppState } from "@auth0/auth0-react";
import { auth0Config } from "../core/config/auth0-config";
import { addAuthInterceptor, removeAuthInterceptor, setBrowserId, BROWSER_ID_STORAGE_KEY } from "../core/api/config/HttpClient";
import jwt_decode from "jwt-decode";

// Type for the JWT token payload
interface TokenPayload {
  [key: string]: any;
}

export interface AuthDetails {
  isLoggedIn: boolean;
  isLoading: boolean;
  token?: string;
  isAdmin: boolean;
  logIn: () => void;
  logout: () => void;
  isTokenReady: boolean;
}

const initialState: AuthDetails = {
  isLoggedIn: false,
  isLoading: true,
  isAdmin: false,
  logIn: () => {},
  logout: () => {},
  isTokenReady: false,
};

const AuthContext = React.createContext<AuthDetails>(initialState);

export const Auth0ProviderWithHistory: FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: auth0Config.scope,
      }}
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      <AuthProviderContent>{children}</AuthProviderContent>
    </Auth0Provider>
  );
};

const AuthProviderContent: FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    loginWithRedirect,
    handleRedirectCallback,
    logout: auth0Logout,
  } = useAuth0();

  const [token, setToken] = React.useState<string | undefined>();
  const [isTokenReady, setIsTokenReady] = React.useState(false);
  const [isUserAdmin, setIsUserAdmin] = React.useState(false);

  // Function to parse the JWT token, extract roles and init browser id
  const parseTokenAndSetRoles = (accessToken: string) => {
    try {
      const decodedToken = jwt_decode<TokenPayload>(accessToken);
      const roles = decodedToken['taz/roles'] || [];
      setIsUserAdmin(Array.isArray(roles) ? roles.includes('ADMIN') : false);

      const userId = decodedToken['preferred_username'] || decodedToken['sub'] || decodedToken['name'];
      if (userId) {

        let browserId = localStorage.getItem(BROWSER_ID_STORAGE_KEY);
        if (!browserId) {
          const randomPrefix = Math.random().toString(36).substring(2, 10);
          const parser = Bowser.getParser(navigator.userAgent);
          const browser = parser.getBrowser().name;
          const os = parser.getOS().name;


          browserId = `${browser}-${os}-${randomPrefix}`;
          localStorage.setItem(BROWSER_ID_STORAGE_KEY, browserId);
        }
        setBrowserId(browserId);
      }
    } catch (error) {
      console.error('Error parsing token:', error);
      setIsUserAdmin(false);
    }
  };

  React.useEffect(() => {
    const handleAuth = async () => {
      // Check if we're handling the redirect callback
      const params = new URLSearchParams(window.location.search);
      const hasAuthParams = params.has('code') && params.has('state');

      if (hasAuthParams) {
        try {
          await handleRedirectCallback();
        } catch (error: any) {
          // If unauthorized, clear any stale tokens and redirect to login
          if (error.message?.includes('Unauthorized') || error.status === 401) {
            // Clear any stored tokens
            localStorage.removeItem('auth0spa');
            await loginWithRedirect({
              appState: { returnTo: window.location.pathname }
            });
          }
        }
      }
      // Only redirect to login if we're sure we're not authenticated and not still loading
      else if (!isLoading && !isAuthenticated && !window.location.pathname.includes('/login')) {
        await loginWithRedirect({
          appState: { returnTo: window.location.pathname }
        });
      }
    };

    handleAuth();
  }, [isLoading, isAuthenticated, loginWithRedirect, handleRedirectCallback]);

  const removeAuth = () => {
    // Remove the token before logging out
    removeAuthInterceptor();
    setBrowserId(null);
    setIsUserAdmin(false);
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
        clientId: auth0Config.clientId
      }
    });
  };

  // Effect to handle token setup and refresh
  React.useEffect(() => {
    let isMounted = true;
    let tokenRetryCount = 0;
    const MAX_RETRIES = 3;

    const getToken = async () => {
      try {
        if (isAuthenticated && !isLoading) {
          setIsTokenReady(false);
          const accessToken = await getAccessTokenSilently({
            authorizationParams: {
              audience: auth0Config.audience,
              scope: auth0Config.scope,
            }
          });

          if (isMounted && accessToken) {
            // Parse the token to extract roles
            parseTokenAndSetRoles(accessToken);

            // First set the token in state
            setToken(accessToken);
            // Then add the interceptor
            addAuthInterceptor(accessToken);
            setIsTokenReady(true);
            tokenRetryCount = 0; // Reset retry count on success
          }
        } else if (!isAuthenticated) {
          // Remove token if not authenticated
          removeAuthInterceptor();
          setBrowserId(null);
          setToken(undefined);
          setIsUserAdmin(false);
          setIsTokenReady(false);
        }
      } catch (error: unknown) {
        const authError = error as { error?: string };
        removeAuthInterceptor();
        setBrowserId(null);
        setToken(undefined);
        setIsUserAdmin(false);
        setIsTokenReady(false);

        // Retry token acquisition if we haven't exceeded max retries
        if (tokenRetryCount < MAX_RETRIES) {
          tokenRetryCount++;
          setTimeout(getToken, 1000 * tokenRetryCount); // Exponential backoff
          return;
        }

        if (authError.error === 'login_required' || authError.error === 'consent_required') {
          loginWithRedirect({
            appState: { returnTo: window.location.pathname }
          });
        }
      }
    };

    // Call getToken immediately if we're authenticated and not loading
    if (isAuthenticated && !isLoading) {
      getToken();
    } else {
      // Clear token if not authenticated
      removeAuthInterceptor();
      setBrowserId(null);
      setToken(undefined);
      setIsUserAdmin(false);
      setIsTokenReady(false);
    }

    // Set up token refresh interval
    const refreshInterval = setInterval(() => {
      if (isAuthenticated && !isLoading) {
        getToken();
      }
    }, 5 * 60 * 1000); // Refresh every 5 minutes

    return () => {
      isMounted = false;
      clearInterval(refreshInterval);
      // Clean up token on unmount
      removeAuthInterceptor();
      setBrowserId(null);
      setToken(undefined);
      setIsUserAdmin(false);
      setIsTokenReady(false);
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect]);

  const logIn = () => {
    loginWithRedirect({
      appState: { returnTo: window.location.pathname }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        logIn,
        logout: removeAuth,
        isAdmin: isUserAdmin,
        token,
        isLoading: isLoading || !isTokenReady,
        isLoggedIn: isAuthenticated,
        isTokenReady,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export the main provider
export const AuthProvider = Auth0ProviderWithHistory;
